// src/components/workflows/views/ResultsSummary.jsx - Results summary for automation dashboard
import React from 'react';
import { BarChart3, FileText, DollarSign, Hash, Clock, CheckCircle } from 'lucide-react';

/**
 * ResultsSummary - Displays key-value summary from last run
 * Automatically renders any fields in result_summary
 *
 * Props:
 *   summary: { [key]: value } - any key-value pairs
 *   lastRunTime: string (ISO timestamp) | null
 */
const ResultsSummary = ({ summary = {}, lastRunTime }) => {
  // Format key to readable label
  const formatLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  // Get icon based on key name
  const getIcon = (key) => {
    const keyLower = key.toLowerCase();
    if (keyLower.includes('count') || keyLower.includes('found') || (keyLower.includes('total') && !keyLower.includes('balance'))) {
      return Hash;
    }
    if (keyLower.includes('balance') || keyLower.includes('amount') || keyLower.includes('price') || keyLower.includes('cost')) {
      return DollarSign;
    }
    if (keyLower.includes('time') || keyLower.includes('duration')) {
      return Clock;
    }
    if (keyLower.includes('success') || keyLower.includes('complete')) {
      return CheckCircle;
    }
    return FileText;
  };

  // Format value for display
  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toLocaleString();
    return String(value);
  };

  const entries = Object.entries(summary).filter(([key]) => !key.startsWith('_'));
  const hasData = entries.length > 0;

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
          <BarChart3 className="w-5 h-5" />
          Last Results
        </h3>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          {lastRunTime ? `Updated ${formatRelativeTime(lastRunTime)}` : 'Results from the most recent automation run.'}
        </p>
      </div>

      {hasData ? (
        <div className="space-y-3">
          {entries.map(([key, value]) => {
            const Icon = getIcon(key);
            return (
              <div
                key={key}
                className="flex items-center justify-between p-4 border border-zinc-700/50 rounded-lg bg-zinc-900 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{formatLabel(key)}</span>
                </div>
                <span className="text-sm text-muted-foreground font-medium">
                  {formatValue(value)}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-8 border border-zinc-700/50 rounded-lg bg-zinc-900/50 text-center">
          <BarChart3 className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No results yet</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Run the automation to see results here</p>
        </div>
      )}
    </div>
  );
};

export default ResultsSummary;
